import './child-bio-details.scss';

import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { HtmlBlock } from '@sola/ark-core/atoms/html/html.js';
import { TabGroup } from '@sola/ark-core/molecules/tab-group/tab-group.js';
import { useCart } from '@sola/cart-sdk/store.js';
import {
  ChildAvailabilityItem,
  ChildDetails,
} from '@sola/schema/dotcom/children/child.js';
import { countryToIso } from '@sola/schema/dotcom/shared/geo.js';

import { useChildSponsorship } from '../../hooks/useChildSponsorship';
import { key } from '../../organisms-cms/child-bio/utils/bioKeys';
import { getNote } from '../../organisms-cms/child-bio/utils/childNote';
import { getAge } from '../../utils/child-bio';
import { ChildBioDetailsHeading } from './child-bio-details-heading';
import { ChildBioDetailsOverview } from './child-bio-details-overview';
import { ChildBioInfo } from './child-bio-info';
import { SubmitButton } from './child-bio-sponsorship-submit-button';
import { ChildBioSummary } from './child-bio-summary';

export const ChildBioDetails = ({
  seoLevel = 1,
  details,
  availability,
}: {
  seoLevel?: number;
  details: ChildDetails;
  availability: ChildAvailabilityItem;
}) => {
  const { t } = useTranslation(['bio', 'geo']);
  const router = useRouter();

  const searchParams = useSearchParams();
  const {
    actions: { addChild, goToCheckout },
  } = useCart('actions');

  const {
    ageInYearsAndMonths,
    country,
    correspondenceLanguage,
    globalId,
    localId,
    name,
    hangul,
  } = details;
  const age = getAge(ageInYearsAndMonths);

  const isToddler = age === '1' || age === '2';

  const countryIso = countryToIso(country, localId);
  const countryKey = String('geo:countries.')
    .concat(countryIso)
    .concat('.nameS');
  const translatedCountry = t(countryKey as unknown as TemplateStringsArray);
  const communityDescription = t('bio:community', { context: countryIso });
  const countryDescription = t('bio:description', { context: countryIso });
  const languageKey = key('bio:language', correspondenceLanguage);
  const translatedLanguage = t(languageKey as unknown as TemplateStringsArray);

  const note = getNote(details);

  const { isSubmitting, bioErrors, handleSponsorshipSubmit } =
    useChildSponsorship(globalId);

  const overviewTab = {
    seoLevel: seoLevel + 1,
    title: t('bio:overview'),
    tabKey: 'overview',
    body: isToddler ? (
      <HtmlBlock
        htmlString={t('bio:babyParagraphs', {
          childName: name.preferred,
          koreanName: hangul.preferred || name.preferred,
        })}
      />
    ) : (
      <ChildBioDetailsOverview seoLevel={seoLevel} details={details} />
    ),
  };
  const communityTab = {
    seoLevel: seoLevel + 1,
    title: t('bio:community'),
    tabKey: 'community',
    body: <HtmlBlock htmlString={communityDescription as string} />,
  };
  const countryTab = {
    seoLevel: seoLevel + 1,
    title: t('bio:country'),
    tabKey: 'country',
    body: <HtmlBlock htmlString={countryDescription as string} />,
  };

  return (
    <div className="ChildBioDetails body-md xl:pr-comfy-xl grid gap-roomy-sm">
      <div className="ChildBioDetails__intro">
        <ChildBioDetailsHeading
          seoLevel={seoLevel}
          hangul={hangul}
          name={name}
        />
        <ChildBioSummary
          details={details}
          translatedCountry={translatedCountry}
          translatedLanguage={translatedLanguage}
        />
        <ChildBioInfo details={details} countryKey={countryKey} note={note} />
        <SubmitButton
          bioErrors={bioErrors}
          className="mt-comfy-xl"
          details={details}
          isSubmitting={isSubmitting}
          handleSponsorshipSubmit={handleSponsorshipSubmit}
        />
      </div>
      <div className="ChildBioDetails__content">
        <TabGroup
          activeValue={overviewTab.tabKey}
          items={[overviewTab, communityTab, countryTab]}
        />
      </div>
    </div>
  );
};
